<template>
  <label :class="rootClasses">
    <input ref="inputRef" class="ui-checkbox__input" type="checkbox" :checked="modelValue"
      :disabled="disabled" :aria-checked="indeterminate ? 'mixed' : (modelValue ? 'true' : 'false')"
      :aria-label="resolvedAriaLabel" @change="handleChange" @focus="emit('focus', $event)"
      @blur="emit('blur', $event)" />
    <span class="ui-checkbox__control" aria-hidden="true">
      <svg v-if="indeterminate" class="ui-checkbox__icon" viewBox="0 0 12 12" fill="none"
        xmlns="http://www.w3.org/2000/svg">
        <path d="M2.5 6H9.5" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" />
      </svg>
      <svg v-else-if="modelValue" class="ui-checkbox__icon" viewBox="0 0 12 12" fill="none"
        xmlns="http://www.w3.org/2000/svg">
        <path d="M2.5 6.2L4.8 8.5L9.5 3.5" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"
          stroke-linejoin="round" />
      </svg>
    </span>
    <span v-if="label || description || $slots.default" class="ui-checkbox__copy">
      <span v-if="label || $slots.default" class="ui-checkbox__label">
        <slot>{{ label }}</slot>
      </span>
      <span v-if="description" class="ui-checkbox__description">{{ description }}</span>
    </span>
  </label>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

const props = withDefaults(defineProps<{
  modelValue?: boolean;
  label?: string;
  description?: string;
  ariaLabel?: string;
  disabled?: boolean;
  indeterminate?: boolean;
}>(), {
  modelValue: false,
  label: '',
  description: '',
  ariaLabel: '',
  disabled: false,
  indeterminate: false
});

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  change: [value: boolean];
  focus: [event: FocusEvent];
  blur: [event: FocusEvent];
}>();

const inputRef = ref<HTMLInputElement | null>(null);

const rootClasses = computed(() => [
  'ui-checkbox',
  {
    'is-checked': props.modelValue && !props.indeterminate,
    'is-indeterminate': props.indeterminate,
    'is-disabled': props.disabled
  }
]);

const resolvedAriaLabel = computed(() => props.ariaLabel || props.label || 'Checkbox');

function handleChange(event: Event): void {
  const target = event.target as HTMLInputElement;
  emit('update:modelValue', target.checked);
  emit('change', target.checked);
}

function focus(): void {
  inputRef.value?.focus();
}

defineExpose({ focus });
</script>
